
import { Component, Input, Output, EventEmitter, Inject, OnChanges, SimpleChanges, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Project, Customer } from '../../../core/services/admin.service';
import { AdminFeedbackService } from '../../../core/services/admin-feedback.service';
import { DialogService } from '../../../core/services/dialog.service';
import { FeatureFlagsService } from '../../../core/services/feature-flags.service';
type ProjectEditModel = Partial<Project>;

@Component({
  selector: 'app-admin-project-tab',
  standalone: false,
  template: require('./admin-project-tab.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./admin-project-tab.component.scss').default],
})
export class AdminProjectTabComponent implements OnChanges {
  private readonly requiredCreateFields: Array<keyof ProjectEditModel> = ['project_name'];
  budgetEnabled = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-group')) {
      this.showMoreMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMoreMenu = false;
  }

  @Input() projects: Project[] = [];
  @Input() customers: Customer[] = [];
  @Output() dataUpdated = new EventEmitter<void>();

  // Filtered list
  filteredProjects: Project[] = [];

  // Filter - object binding for ng-select
  selectedCustomerFilter: Customer | null = null;

  // Form field selection - object binding for ng-select
  selectedEditCustomer: Customer | null = null;

  // Current selection
  currentIndex = 0;
  selectedProject: Project | null = null;
  editProject: ProjectEditModel = {};
  initialEditProject: ProjectEditModel = {};
  originalId: number | null = null;

  // New record mode
  isNew = false;

  // Dirty flag
  isDirty = false;

  // Dropdown menu toggle
  showMoreMenu = false;
  private pendingSelectionId: number | null = null;
  private pendingSelectionIndex: number | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(AdminFeedbackService) private adminFeedback: AdminFeedbackService,
    @Inject(FeatureFlagsService) private featureFlags: FeatureFlagsService
  ) {
    this.featureFlags.getFlags$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((flags) => {
      this.budgetEnabled = !!flags.projectBudgetInTime;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projects'] || changes['customers']) {
      if (this.projects.length > 0) {
        // Default to first customer
        if (this.customers.length > 0 && this.selectedCustomerFilter === null) {
          this.selectedCustomerFilter = this.customers[0];
        }
        this.applyFilters();
      }
    }
  }

  applyFilters(): void {
    if (this.selectedCustomerFilter === null) {
      this.filteredProjects = [...this.projects];
    } else {
      this.filteredProjects = this.projects.filter((p) => p.customer_id === this.selectedCustomerFilter?.customer_id);
    }

    // Never let background refreshes or filter list updates silently switch the
    // form out of "new" or dirty edit mode. That can otherwise turn a create
    // flow into an update against the currently auto-selected project.
    if (this.isNew || this.isDirty) {
      if (!this.isNew && this.selectedProject) {
        const selectedIndex = this.filteredProjects.findIndex((p) => p.project_id === this.selectedProject?.project_id);
        this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      } else if (this.filteredProjects.length === 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = Math.min(this.currentIndex, this.filteredProjects.length - 1);
      }

      this.pendingSelectionId = null;
      this.pendingSelectionIndex = null;
      return;
    }

    if (this.filteredProjects.length > 0) {
      const preferredId = this.pendingSelectionId ?? this.originalId ?? this.selectedProject?.project_id ?? null;
      const preferredIndex =
        preferredId === null ? -1 : this.filteredProjects.findIndex((p) => p.project_id === preferredId);

      if (preferredIndex >= 0) {
        this.currentIndex = preferredIndex;
        this.selectProject(this.filteredProjects[preferredIndex]);
      } else if (this.pendingSelectionIndex !== null) {
        const clampedIndex = Math.min(this.pendingSelectionIndex, this.filteredProjects.length - 1);
        this.currentIndex = clampedIndex;
        this.selectProject(this.filteredProjects[clampedIndex]);
      } else {
        this.currentIndex = 0;
        this.selectProject(this.filteredProjects[0]);
      }
    } else {
      this.selectedProject = null;
      this.editProject = {};
      this.currentIndex = 0;
    }

    this.pendingSelectionId = null;
    this.pendingSelectionIndex = null;
  }

  selectProject(proj: Project): void {
    this.selectedProject = proj;
    this.originalId = proj.project_id;
    this.editProject = { ...proj };
    this.initialEditProject = { ...proj };
    // Set selected customer object for ng-select
    this.selectedEditCustomer = this.customers.find(c => c.customer_id === proj.customer_id) || null;
    this.isNew = false;
    this.isDirty = false;
  }

  onCustomerFilterChange(): void {
    this.applyFilters();
  }

  onEditCustomerChange(): void {
    this.editProject.customer_id = this.selectedEditCustomer?.customer_id;
    this.onFieldChange();
  }

  // Called when user selects a project from ng-select
  onProjectSelect(): void {
    if (this.selectedProject) {
      const index = this.filteredProjects.findIndex(p => p.project_id === this.selectedProject?.project_id);
      if (index >= 0) {
        this.currentIndex = index;
      }
      this.selectProject(this.selectedProject);
    }
  }

  // Navigation
  movePrev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.selectProject(this.filteredProjects[this.currentIndex]);
    }
  }

  moveNext(): void {
    if (this.currentIndex < this.filteredProjects.length - 1) {
      this.currentIndex++;
      this.selectProject(this.filteredProjects[this.currentIndex]);
    }
  }

  // Actions
  addNew(): void {
    this.isNew = true;
    this.selectedProject = null;
    this.originalId = null;
    // Set default customer for ng-select
    this.selectedEditCustomer = this.selectedCustomerFilter || this.customers[0] || null;
    this.editProject = {
      project_name: '',
      customer_id: this.selectedEditCustomer?.customer_id,
    };
    this.initialEditProject = { ...this.editProject };
    this.isDirty = false;
  }

  save(): void {
    const payload: ProjectEditModel = { ...this.editProject };
    if (!this.budgetEnabled) {
      delete payload.budget_hours;
    }

    if (this.isNew) {
      this.adminService.createProject(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          this.pendingSelectionId = result?.insertId ?? null;
          this.isNew = false;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('skapa', 'projekt', err),
      });
    } else if (this.originalId !== null) {
      this.adminService.updateProject(this.originalId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.pendingSelectionId = this.originalId;
          this.isDirty = false;
          this.dataUpdated.emit();
        },
        error: (err) => this.adminFeedback.showCrudError('uppdatera', 'projekt', err),
      });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.isNew = false;
      if (this.filteredProjects.length > 0) {
        this.selectProject(this.filteredProjects[this.currentIndex]);
      } else {
        this.editProject = {};
        this.initialEditProject = {};
        this.selectedEditCustomer = null;
      }
    } else if (this.selectedProject) {
      this.selectProject(this.selectedProject);
      return;
    }
    this.isDirty = false;
  }

  async delete(): Promise<void> {
    if (this.originalId === null) {
      return;
    }

    const confirmed = await this.dialogService.confirm({
      title: 'Bekräfta borttagning',
      body: 'Är du säker på att du vill ta bort detta projekt?',
      confirmText: 'Ta bort',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.pendingSelectionIndex = this.currentIndex;
    this.adminService.deleteProject(this.originalId!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dataUpdated.emit();
      },
      error: (err) => this.adminFeedback.showCrudError('ta bort', 'projekt', err),
    });
  }

  duplicate(): void {
    if (!this.selectedProject) return;
    this.isNew = true;
    this.originalId = null;
    this.editProject = {
      ...this.selectedProject,
      project_id: undefined,
      project_name: this.selectedProject.project_name + ' (kopia)',
    };
    this.initialEditProject = { ...this.selectedProject };
    this.selectedEditCustomer = this.customers.find(c => c.customer_id === this.editProject.customer_id) || null;
    this.isDirty = true;
  }

  onFieldChange(): void {
    this.isDirty = this.hasChanges();
  }

  isRequiredField(field: keyof ProjectEditModel): boolean {
    return this.requiredCreateFields.includes(field);
  }

  isRequiredMissing(field: keyof ProjectEditModel): boolean {
    if (!this.isNew || !this.isRequiredField(field)) {
      return false;
    }
    const value = this.editProject[field];
    return typeof value === 'string' ? value.trim() === '' : value == null;
  }

  hasMissingRequiredFields(): boolean {
    return this.requiredCreateFields.some((field) => this.isRequiredMissing(field));
  }

  isFieldDirty(field: keyof ProjectEditModel): boolean {
    return this.normalizeFieldValue(this.editProject[field]) !== this.normalizeFieldValue(this.initialEditProject[field]);
  }

  hasChanges(): boolean {
    const fields = new Set<keyof ProjectEditModel>([
      ...Object.keys(this.editProject),
      ...Object.keys(this.initialEditProject),
    ] as Array<keyof ProjectEditModel>);
    return Array.from(fields).some((field) => this.isFieldDirty(field));
  }

  private normalizeFieldValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  canSave(): boolean {
    if (!this.isDirty) {
      return false;
    }
    if (this.hasBudgetError()) {
      return false;
    }
    if (!this.isNew) {
      return true;
    }
    return this.requiredCreateFields.every((field) => !this.isRequiredMissing(field));
  }

  canUndo(): boolean {
    return this.isDirty || this.isNew;
  }

  canAddNew(): boolean {
    return !this.isNew && !this.isDirty;
  }

  canOpenMoreMenu(): boolean {
    return !this.isNew && !this.isDirty && !!this.selectedProject;
  }

  get budgetHoursInput(): string {
    if (this.editProject.budget_hours == null || this.editProject.budget_hours === undefined) {
      return '';
    }
    return String(this.editProject.budget_hours);
  }

  set budgetHoursInput(value: string | number | null) {
    if (value === null || value === undefined || value === '') {
      this.editProject.budget_hours = null;
      return;
    }
    const normalized = typeof value === 'number' ? value : value.trim();
    this.editProject.budget_hours = normalized === '' ? null : Number(normalized);
  }

  hasBudgetError(): boolean {
    if (!this.budgetEnabled) {
      return false;
    }
    const value = this.editProject.budget_hours;
    if (value === null || value === undefined) {
      return false;
    }
    return typeof value !== 'number' || !Number.isFinite(value) || value <= 0;
  }

  getCustomerName(customerId: number | undefined): string {
    if (!customerId) return '';
    const cust = this.customers.find((c) => c.customer_id === customerId);
    return cust?.customername || '';
  }
}
